import { WhereItIs } from "../components/WhereItIs";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { useAuth } from "@/context/AuthContext";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { PhotoFixAction, PhotoMoveTrail, RemovedPhotosStrip } from "@/components/PhotoFixAction";
import { SourceTruckAction, SourceTruckLine } from "@/components/SourceTruckAction";

import { FloorActionButton } from "@/floor/FloorActionButton";
import { FloorScanOverlay } from "@/floor/FloorScanOverlay";
import { FloorPhotoChoice } from "@/floor/FloorPhotoChoice";
import { NoPhotoBypassDialog } from "@/components/NoPhotoBypass";
import { usePhotoSources } from "@/components/PhotoSourceButtons";
import { useFlash } from "@/floor/useFlash";
import { useFloorActions } from "@/lib/floor";
import { useGoDesk } from "@/lib/floorReturn";
import { ArrowLeft, MapPin, Package, Loader2, ExternalLink, ShoppingCart, Scissors, X, CameraOff } from "lucide-react";

// Model C job labels (mirror the desk AddToCartButton) — floor shows OPEN jobs only.
const jobTitle = (c) => c.assignment_type === "build" ? (c.build?.name || "Build")
  : c.assignment_type === "customer_order" ? (c.customer_order?.customer_name || "Customer Order") : "Internal Use";
const jobSub = (c) => c.assignment_type === "build" ? (c.build?.po_number ? `PO ${c.build.po_number}` : "no PO")
  : c.assignment_type === "customer_order" ? (c.customer_order?.walk_in ? "Walk-in" : `Ref ${c.customer_order?.order_ref || c.customer_order?.po_number || "—"}`)
  : (c.internal_use?.purpose || "Internal");

// Floor status = only what changes a floor decision: Available, Claimed (reserved/in a
// cart — don't grab it), or Gone. Desk keeps the full workflow vocabulary untouched.
function floorStatus(part) {
  if (part.available) return { label: "Available", cls: "bg-emerald-600 text-white" };
  const s = (part.status || "").toLowerCase();
  if (/(cart|reserv|hold|pick|claim)/.test(s)) return { label: "Claimed", cls: "bg-amber-500 text-slate-900" };
  if (/(sold|gone|ship|scrap|deple)/.test(s)) return { label: "Gone", cls: "bg-slate-800 text-white" };
  return { label: "Not available", cls: "bg-slate-500 text-white" };
}

export default function FloorPartCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { has } = useAuth();
  const goDesk = useGoDesk();
  const [part, setPart] = useState(null);
  const [shotIdx, setShotIdx] = useState(null);   // the one photo viewer
  const [overlay, setOverlay] = useState(null); // 'move' | 'retag'
  const [jobPicker, setJobPicker] = useState(false);
  const [jobs, setJobs] = useState(null);       // null = loading; [] = none open
  const [busy, setBusy] = useState(false);
  const [noPhoto, setNoPhoto] = useState(null);   // no tag/photo yet: alert, then the logged bypass
  const { trigger, FlashOverlay } = useFlash();
  const actions = useFloorActions("part");

  const load = async () => {
    try { const { data } = await api.get(`/parts/${id}`); setPart(data); }
    catch { setPart(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const livePhotos = (part?.photos || []).filter((p) => p.file_id && !p.is_archived && !p.bytes_missing);
  // its MAIN photo (Batch 3b) — never a tag close-up by default; none means "No overall photo yet"
  const firstPhoto = livePhotos.find((p) => p.file_id === part?.main_photo?.file_id) || null;
  const loc = part?.location;
  const hasLoc = !!loc;

  // --- actions -----------------------------------------------------------
  const doMove = async (binCode, meta) => {
    setBusy(true);
    try {
      const { data } = await api.post("/putaway", { part_scan: part.short_code, location_id: binCode,
                                                    provenance: prov("floor_part_card", meta) });
      if (data.already) { trigger("warning", "ALREADY THERE"); }
      else if (!data.ok) { trigger("error", data.message || "MOVE FAILED"); return; }
      else { trigger("success", `MOVED · ${data.location?.short_code || binCode}`); }
      setOverlay(null); await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "MOVE FAILED"); }
    finally { setBusy(false); }
  };

  const doRetag = async (newTag) => {
    setBusy(true);
    try {
      await api.post(`/parts/${id}/retag`, { new_tag_code: newTag });
      trigger("success", "RE-TAGGED"); setOverlay(null); await load();
    } catch (e) { trigger("error", apiErrorMessage(e) || "RE-TAG FAILED"); }
    finally { setBusy(false); }
  };

  const savePhotos = async (files) => {
    setBusy(true);
    try {
      let dup = 0;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post(`/parts/${id}/photos`, fd, { timeout: 120000 });
        if (data.duplicate) dup += 1;
      }
      const saved = files.length - dup;
      trigger("success", saved === 0 ? "ALREADY HAVE IT" : saved > 1 ? `${saved} PHOTOS SAVED` : "PHOTO SAVED"); await load();
    } catch (e2) { trigger("error", apiErrorMessage(e2) || "PHOTO FAILED"); }
    finally { setBusy(false); }
  };
  const photo = usePhotoSources({ onFiles: savePhotos, testidPrefix: "floor-part-photo" });

  const canRetag = has("scrap_writeoff");

  // Add to Job (Model C) — big-button, OPEN JOBS ONLY. "New job" is a desk task; if there
  // are no open jobs the tech is told to make one at a desk. Same /carts/add-to-job engine.
  const openJobPicker = async () => {
    setJobPicker(true); setJobs(null);
    try {
      const { data } = await api.get("/carts", { params: { status: "draft" } });
      setJobs((data || []).filter((c) => !c.sample_purged));
    } catch { setJobs([]); }
  };
  const addToJob = async (cartId, bypass) => {
    setBusy(true);
    try {
      const { data } = await api.post("/carts/add-to-job", { part_id: id, cart_id: cartId, ...(bypass ? {
        floor_override: true, floor_override_reason: bypass.reason, floor_override_note: bypass.note } : {}) });
      trigger("success", `ADDED · ${data.cart_number || "job"}${bypass ? " · NO PHOTO, LOGGED" : ""}`); setJobPicker(false); setNoPhoto(null); await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "field_add_blocked" && d.override_available) { setNoPhoto({ ...d, cartId }); setJobPicker(false); }
      else if (d && typeof d === "object" && d.code === "already_on_this_job") { trigger("warning", d.message); setJobPicker(false); }
      else trigger("error", apiErrorMessage(e) || "COULDN'T ADD IT — TAP THE JOB AGAIN");
    }
    finally { setBusy(false); }
  };

  const HANDLERS = {
    part_photo: () => setOverlay("photo"),
    part_move: () => setOverlay("move"),
    part_add_job: openJobPicker,
    // "Tag / Re-scan": normally just scan the next item. Only becomes an actual RE-TAG
    // when the part is flagged needs_retag AND the tech has the tag-lifecycle permission
    // (retag changes a part's identity — a controlled correction, not an everyday floor tap).
    part_rescan: () => { if (part?.needs_retag && canRetag) setOverlay("retag"); else navigate("/floor"); },
  };

  if (part === false) return <NotFound onBack={() => navigate("/floor")} label="part" />;
  if (!part) return <div className="flex-1 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex-1 flex flex-col" data-testid="floor-part-card">
      {FlashOverlay}
      {photo.inputs}
      <FloorPhotoChoice open={overlay === "photo"} testid="floor-part-photo-choice"
        onCamera={photo.openCamera} onGallery={photo.openGallery} onClose={() => setOverlay(null)} />
      <PhotoLightbox photos={livePhotos} index={shotIdx} onIndex={setShotIdx} onClose={() => setShotIdx(null)}
        testidPrefix="floor-part-photos" fixScope="part" fixRecordId={id} onFixed={load} />

      <div className="p-4 sm:p-6 flex flex-col gap-5 flex-1">
        <button data-testid="floor-back" onClick={() => navigate("/floor")}
          className="flex items-center gap-1.5 font-bold text-[#B01E28] text-lg self-start">
          <ArrowLeft className="w-5 h-5" /> Scan
        </button>

        {/* CONFIRM STRIP — only enough to confirm "right part?" */}
        <div className="flex gap-4 items-center">
          <div className="shrink-0 flex flex-col items-center gap-1.5">
            {/* "RIGHT PART?" IS ANSWERED BY LOOKING. The photo opens the one viewer — zoom,
                rotate, drag, next/previous — instead of being a dead 28-pixel square. */}
            <button type="button" data-testid="floor-part-photo-open" disabled={!livePhotos.length}
              onClick={() => livePhotos.length && setShotIdx(firstPhoto ? livePhotos.findIndex((x) => x.file_id === firstPhoto.file_id) : 0)}
              className="w-28 h-28 rounded-xl border-2 border-slate-200 overflow-hidden bg-slate-100 disabled:cursor-default">
              {firstPhoto ? <AuthImg fileId={firstPhoto.file_id} thumb className="w-full h-full object-cover" />
                : livePhotos.length
                  ? <div data-testid="floor-part-photo-no-overall" className="w-full h-full flex flex-col items-center justify-center gap-1 bg-amber-50 text-amber-700 text-xs font-semibold text-center leading-tight px-2"><CameraOff className="w-6 h-6" />No overall photo yet</div>
                  : <div className="w-full h-full flex items-center justify-center"><Package className="w-10 h-10 text-slate-300" /></div>}
            </button>
            {firstPhoto && (
              <PhotoFixAction scope="part" recordId={id} photo={firstPhoto} onDone={load} big
                testid="floor-part-photo-fix" label="Wrong photo?" />
            )}
          </div>
          <div className="min-w-0">
            <div data-testid="floor-part-name" className="font-display text-3xl font-black tracking-tight leading-tight truncate">{part.name || "Unidentified part"}</div>
            <div className="font-mono text-lg text-slate-500 mt-0.5">{part.short_code}</div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`inline-flex items-start gap-1.5 rounded-lg px-3 py-1.5 max-w-full ${hasLoc ? "bg-amber-400 text-slate-900" : "bg-slate-200 text-slate-500"}`} data-testid="floor-part-location">
                <WhereItIs location={loc} size="xl" icon={false} testid="floor-part-where"
                  fallback={part?.location_missing ? "ITS PLACE IS GONE — SCAN IT ONTO A SHELF" : "No location"} />
              </span>
              <span className={`rounded-lg px-3 py-1 text-base font-bold uppercase tracking-wide ${floorStatus(part).cls}`} data-testid="floor-part-status">
                {floorStatus(part).label}
              </span>
            </div>
          </div>
        </div>

        <PhotoMoveTrail scope="part" recordId={id} onDone={load} testid="floor-part-moves" />

        <RemovedPhotosStrip scope="part" recordId={id} photos={part.photos} onDone={load}
          testid="floor-part-removed" />

        {part.needs_retag && (
          <div className="rounded-xl bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 text-lg font-bold" data-testid="floor-part-needs-retag">
            {canRetag ? "Needs a new tag — tap “Tag / Re-scan”." : "Needs a new tag — a lead can re-tag this at a desk."}
          </div>
        )}

        {/* WHICH TRUCK IT CAME OFF — on the floor, "since that's where someone recognises which
            truck a part came off" (his instinct, and the only place the recognition happens). */}
        <SourceTruckLine part={part} onDone={load} testid="floor-part-source-line" />
        <SourceTruckAction part={part} onDone={load} big testid="floor-part-source" />

        {/* Escape hatch — full desk record (identify, keywords, numbers, links); returns to Floor */}
        <button data-testid="floor-part-full-detail" onClick={() => goDesk(`/parts/${id}`)}
          className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-700 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform duration-100">
          <ExternalLink className="w-5 h-5 text-[#B01E28]" /> See Full Detail
        </button>

        {/* Second-tier: Parent/Child split is a deliberate desk-grade action (choose what
            identity moves), so it opens the hardened split tool in the full record and
            returns to Floor. Kept OUT of the primary action grid on purpose. */}
        {has("manage_parts") && (
          <button data-testid="floor-part-split" onClick={() => goDesk(`/parts/${id}?split=1`)}
            className="w-full h-12 rounded-xl border-2 border-slate-200 bg-white font-bold text-slate-700 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform duration-100">
            <Scissors className="w-5 h-5 text-[#B01E28]" /> Split / Parent-Child
          </button>
        )}

        {/* ACTION ZONE — dominant, owner-ordered, floor language */}
        <div className="mt-auto grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="floor-part-actions">
          {actions.map((a) => (
            <FloorActionButton key={a.code} testid={`floor-action-${a.code}`} label={a.label}
              icon={a.icon} tone={a.tone} busy={busy && overlay === null} onClick={HANDLERS[a.code] || (() => {})} />
          ))}
        </div>
      </div>

      <FloorScanOverlay open={overlay === "move"} testid="floor-move-overlay"
        title="Scan destination bin" sub={part.name || part.short_code} submitLabel="Move here"
        onScan={doMove} onClose={() => setOverlay(null)} busy={busy} />
      <FloorScanOverlay open={overlay === "retag"} testid="floor-retag-overlay"
        title="Scan a fresh tag" sub={`Re-tag ${part.short_code}`} submitLabel="Assign tag"
        onScan={doRetag} onClose={() => setOverlay(null)} busy={busy} />

      <NoPhotoBypassDialog block={noPhoto} onClose={() => setNoPhoto(null)} big busy={busy}
        actionLabel="Add to the job — logged" after="The photo is still needed before the job is signed off."
        onConfirm={(r) => addToJob(noPhoto.cartId, r)} />

      {jobPicker && (
        <div className="fixed inset-x-0 top-vv h-vv z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-6" data-testid="floor-job-picker"
          onClick={() => !busy && setJobPicker(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-kb flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div className="font-display text-2xl font-black">Add to a job</div>
              <button data-testid="floor-job-close" onClick={() => setJobPicker(false)} className="p-1"><X className="w-7 h-7 text-slate-400" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex flex-col gap-3">
              {jobs === null && <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>}
              {jobs && jobs.length === 0 && (
                <div className="rounded-xl border-2 border-dashed border-slate-200 p-6 text-center text-lg font-bold text-slate-500" data-testid="floor-job-empty">
                  No open jobs — create one at a desk.
                </div>
              )}
              {(jobs || []).map((c) => (
                <button key={c.id} data-testid={`floor-job-${c.id}`} disabled={busy} onClick={() => addToJob(c.id)}
                  className="rounded-xl border-2 border-[#E2E8F0] bg-white p-4 flex items-center justify-between text-left active:scale-[0.99] active:bg-slate-50 disabled:opacity-50 transition-[transform,background-color] duration-100">
                  <div className="min-w-0">
                    <div className="font-bold text-lg truncate">{jobTitle(c)}</div>
                    <div className="text-base text-slate-500 truncate">{c.cart_number} · {jobSub(c)}</div>
                  </div>
                  <span className="text-sm text-slate-400 shrink-0 ml-2">{c.line_count} parts</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotFound({ onBack, label }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6" data-testid="floor-card-notfound">
      <div className="text-2xl font-black text-slate-700">This {label} wasn’t found.</div>
      <FloorActionButton label="Back to scan" icon="ScanLine" tone="primary" onClick={onBack} testid="floor-notfound-back" />
    </div>
  );
}
